import { on } from '@ngrx/store';

import { LoadingState } from 'src/app/utilities/state.util';

import { BlueSageSettingsActions } from './bluesage-settings.actions';
import { BlueSageSettingsState } from './bluesage-settings.state';

/**
 * Requirement: K3/K4 - the server's setup status replaces ours wholesale on every
 * success, so the panel can never drift from what the zone actually holds.
 *
 * Every successful call returns the whole setup status, so the reducer never has
 * to patch fields individually — the server's answer replaces ours. That is what
 * keeps "what the screen says" and "what the zone actually has" in step.
 */
const applyStatus = (
    state: BlueSageSettingsState,
    { status }: { status: BlueSageSettingsState['status'] },
): BlueSageSettingsState => ({
    ...state,
    status,
    loading: LoadingState.LOADED,
    saving: false,
    // Show the stored location id rather than whatever was last typed.
    locationId: status?.locationId ?? '',
    // Clear the secret inputs so a stale value cannot be resubmitted by accident.
    apiKey: '',
    webhookSigningSecret: '',
});

export const blueSageSettingsReducers = [

    on(
        BlueSageSettingsActions.panelOpened,
        BlueSageSettingsActions.refreshButtonClicked,
        (state: BlueSageSettingsState): BlueSageSettingsState => ({
            ...state,
            loading: LoadingState.LOADING,
        }),
    ),

    on(
        BlueSageSettingsActions.loadSetupSuccess,
        applyStatus,
    ),

    on(
        BlueSageSettingsActions.loadSetupError,
        (state: BlueSageSettingsState): BlueSageSettingsState => ({
            ...state,
            loading: LoadingState.LOADED,
        }),
    ),

    on(
        BlueSageSettingsActions.apiKeyChanged,
        (state: BlueSageSettingsState, { apiKey }): BlueSageSettingsState => ({ ...state, apiKey }),
    ),

    on(
        BlueSageSettingsActions.webhookSigningSecretChanged,
        (state: BlueSageSettingsState, { webhookSigningSecret }): BlueSageSettingsState =>
            ({ ...state, webhookSigningSecret }),
    ),

    on(
        BlueSageSettingsActions.locationIdChanged,
        (state: BlueSageSettingsState, { locationId }): BlueSageSettingsState => ({ ...state, locationId }),
    ),

    on(
        BlueSageSettingsActions.saveCredentialsButtonClicked,
        BlueSageSettingsActions.saveLocationButtonClicked,
        BlueSageSettingsActions.paymentTypeSwitchToggled,
        (state: BlueSageSettingsState): BlueSageSettingsState => ({ ...state, saving: true }),
    ),

    on(
        BlueSageSettingsActions.saveCredentialsSuccess,
        BlueSageSettingsActions.saveLocationSuccess,
        BlueSageSettingsActions.setEnabledSuccess,
        applyStatus,
    ),

    on(
        BlueSageSettingsActions.saveCredentialsError,
        BlueSageSettingsActions.saveLocationError,
        BlueSageSettingsActions.setEnabledError,
        (state: BlueSageSettingsState): BlueSageSettingsState => ({
            ...state,
            saving: false,
            // Deliberately keep what was typed: a failed save should not make the
            // operator retype a secret they cannot read back from anywhere.
        }),
    ),
];
